import React from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { AuthProvider } from "./auth/AuthContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { AppShell } from "./components/shell/AppShell";

import LoginPage from "./pages/Login";
import LandingPage from "./pages/Landing";
import PrivacyPage from "./pages/Privacy";
import TermsPage from "./pages/Terms";
import MethodologyPage from "./pages/Methodology";
import DashboardPage from "./pages/Dashboard";
import AnalysesListPage from "./pages/AnalysesList";
import RunDetailPage from "./pages/RunDetail";
import ProcessDetailPage from "./pages/ProcessDetail";
import FindingsInboxPage from "./pages/FindingsInbox";
import SettingsPage from "./pages/Settings";
import ImportWizardPage from "./pages/ImportWizard";
import BIAsPage from "./pages/BIAs";
import StrategyInboxPage from "./pages/StrategyInbox";
import RemediationInboxPage from "./pages/RemediationInbox";
import ReportsPage from "./pages/Reports";
import RunComparisonPage from "./pages/RunComparison";
import NoWorkspacePage from "./pages/NoWorkspace";
import { EmptyState } from "./components/states/EmptyState";
import { Link } from "react-router-dom";
import { useAuth } from "./auth/AuthContext";

/**
 * Root — router + auth provider + app shell.
 *
 * Route map:
 *   /login                            (public)
 *   /methodology                      (public)
 *   /w/:workspaceId/dashboard         (protected)
 *   /w/:workspaceId/analyses          (protected)
 *   /w/:workspaceId/analyses/:runId   (protected)
 *   /w/:workspaceId/analyses/:runId/processes/:processId  (protected)
 *   /w/:workspaceId/findings          (protected)
 *   /w/:workspaceId/import            (protected)
 *   /w/:workspaceId/settings          (protected)
 *   /w/:workspaceId/bias              (protected placeholder)
 *   /                                 → redirects to /methodology (public)
 */

function NotFoundPage() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <EmptyState
        title="Not found"
        description="This URL doesn't match any page. Sign in and open a workspace to continue."
        action={
          <Link
            to="/login"
            className="rounded bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
          >
            Go to sign-in
          </Link>
        }
      />
    </div>
  );
}

/**
 * Phase 7a — `/` routing.
 *
 * Authenticated users go to their last (first-listed) workspace dashboard,
 * preserving the pre-Phase-7 behaviour. Unauthenticated users see the
 * public landing page. While the session probe is in-flight, we render
 * the landing page so the marketing surface is not blocked by API latency.
 */
function RootRoute() {
  const { user, status } = useAuth();
  if (status === "authed" && user?.memberships?.length) {
    return (
      <Navigate to={`/w/${user.memberships[0].workspace_id}/dashboard`} replace />
    );
  }
  return <LandingPage />;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public */}
          <Route path="/" element={<RootRoute />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/methodology" element={<MethodologyPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />

          {/* Authenticated fallback for users with zero workspace
              memberships (freshly-bootstrapped platform admin, or a user
              waiting to be invited to a workspace). Rendered outside
              AppShell because AppShell is workspace-scoped. */}
          <Route
            path="/no-workspace"
            element={
              <ProtectedRoute>
                <NoWorkspacePage />
              </ProtectedRoute>
            }
          />

          {/* Protected workspace-scoped shell */}
          <Route
            path="/w/:workspaceId"
            element={
              <ProtectedRoute>
                <AppShell />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="analyses" element={<AnalysesListPage />} />
            <Route path="analyses/:runId" element={<RunDetailPage />} />
            <Route
              path="analyses/:runA/compare/:runB"
              element={<RunComparisonPage />}
            />
            <Route
              path="analyses/:runId/processes/:processId"
              element={<ProcessDetailPage />}
            />
            <Route path="findings" element={<FindingsInboxPage />} />
            <Route path="strategies" element={<StrategyInboxPage />} />
            <Route path="remediation" element={<RemediationInboxPage />} />
            <Route path="reports" element={<ReportsPage />} />
            <Route
              path="analyses/:runId/processes/:processId/strategies"
              element={<ProcessDetailPage />}
            />
            <Route path="import" element={<ImportWizardPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="bias" element={<BIAsPage />} />
          </Route>

          {/* Catch-all */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
